import React from 'react';
import { Spin } from 'antd';
import { useGetFactsheets } from '../hooks/useGetFactsheets';
import { getUserToken, notifyUser } from '../util/util';
import { useModalWidth } from '../hooks/useModalWidth';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

async function downloadPdfWithAuth(url, filename) {
  try {
    const token = getUserToken();
    if (!token) {
      notifyUser('error', 'Please log in to download documents', 'Authentication Required');
      return;
    }

    // Ensure we use HTTPS from the start to avoid redirect issues
    let secureUrl = url;
    if (url.startsWith('http://')) {
      secureUrl = url.replace('http://', 'https://');
    }

    const response = await fetch(secureUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      }
    });

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'factsheet.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Download error:', error);
    notifyUser('error', 'Failed to download document', 'Download Error');
  }
}

function SimpleFactsheetModal({ isOpen, onClose }) {
  const modalWidth = useModalWidth();
  const { data: factsheetsData, isLoading: loadingFactsheets, error: factsheetsError, refetch } = useGetFactsheets(isOpen);

  React.useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.18)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 18,
          minWidth: 320,
          maxWidth: modalWidth,
          width: '100%',
          boxShadow: '0 8px 40px 0 rgba(0,0,0,0.13)',
          padding: 0,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '80vh',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '24px 32px 0 32px' }}>
          <div className="modal-header-title" style={{ fontWeight: 800, color: '#222', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Fact Sheets</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#888', cursor: 'pointer', marginLeft: 12, fontWeight: 700, lineHeight: 1 }} aria-label="Close">×</button>
          </div>
          <p style={{ color: '#888', fontSize: 15, marginBottom: 18, marginTop: 0 }}>Download available fact sheets</p>
        </div>

        {/* Scrollable body */}
        <div style={{ padding: '0 32px 24px 32px', overflowY: 'auto', paddingBottom: 32 }}>
          {/* Factsheets List */}
          <div className="factsheet-simple-list" style={{ marginBottom: 12 }}>
            <div className="factsheet-simple-title" style={{ fontWeight: 700, marginBottom: 8 }}>
              Available Fact Sheets
            </div>

            {loadingFactsheets ? (
              <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
            ) : factsheetsError ? (
              <div style={{ color: '#666', padding: 16, background: '#f8f9fa', borderRadius: 8, textAlign: 'center' }}>
                {factsheetsError.response?.data?.message || factsheetsError?.message || 'No fact sheets available.'}
              </div>
            ) : (() => {
              // Handle both single object and array responses
              let factsheets = [];
              
              if (factsheetsData?.success) {
                if (Array.isArray(factsheetsData.data)) {
                  // If data is an array
                  factsheets = factsheetsData.data;
                } else if (factsheetsData.data && factsheetsData.data.id) {
                  // If data is a single object
                  factsheets = [factsheetsData.data];
                }
              }
              
              console.log('Factsheets data:', factsheetsData); // Debug log
              console.log('Factsheets array:', factsheets); // Debug log

              if (factsheets.length === 0) {
                return <div style={{ color: '#666' }}>No fact sheets available at the moment.</div>;
              }

              // Sort factsheets by creation date (newest first)
              const sortedFactsheets = [...factsheets].sort((a, b) =>
                new Date(b.created_at) - new Date(a.created_at)
              );

              return (
                <div className="factsheet-items-container" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {sortedFactsheets.map(factsheet => (
                    <div key={factsheet.id} className="factsheet-simple-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f2f4f6', padding: 16, borderRadius: 8 }}>
                      <div className="factsheet-simple-meta" style={{ flex: 1, minWidth: 0 }}>
                        <div className="factsheet-simple-scheme" style={{ fontWeight: 700, marginBottom: 4, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                          {factsheet.scheme}
                        </div>
                        <div className="factsheet-simple-detail" style={{ color: '#666', fontSize: 13, marginBottom: 2 }}>
                          <span>
                            <strong>Year:</strong> {factsheet.year}
                          </span>
                        </div>
                        {factsheet.month && (
                          <div className="factsheet-simple-detail" style={{ color: '#666', fontSize: 13, marginBottom: 2 }}>
                            <span>
                              <strong>Month:</strong> {factsheet.month}
                            </span>
                          </div>
                        )}
                        <div className="factsheet-simple-detail" style={{ color: '#666', fontSize: 13 }}>
                          <span>
                            <strong>Added:</strong> {formatDate(factsheet.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="factsheet-simple-actions">
                        {factsheet.download_link && (
                          <button 
                            className="btn btn-primary factsheet-simple-download" 
                            onClick={() => downloadPdfWithAuth(factsheet.download_link, `factsheet-${factsheet.scheme.replace(/\s+/g, '-').toLowerCase()}-${factsheet.year}.pdf`)}
                            style={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              fontWeight: 600
                            }}
                          >
                            <i className="bi bi-download" style={{ fontSize: 16 }}></i>
                            Download
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SimpleFactsheetModal;
